import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookOpen, CircleHelp, Search } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { visibleHelpArticles, searchHelpArticles } from "@/help/helpContent";
import HelpArticle from "./HelpArticle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const COPY = {
  de: {
    title: "Hilfe & Anleitung",
    description: "Schritt für Schritt – passend zu Ihrer aktuellen Seite.",
    search: "Hilfe durchsuchen…",
    context: "Hilfe zu dieser Seite",
    all: "Weitere Anleitungen",
    steps: "Schritt für Schritt",
    tips: "Wichtige Hinweise",
    next: "Nächster Schritt",
    center: "Gesamtes Hilfe-Center öffnen",
    empty: "Keine passende Anleitung gefunden.",
  },
  en: {
    title: "Help & guidance",
    description: "Step by step, matched to your current page.",
    search: "Search help…",
    context: "Help for this page",
    all: "More guides",
    steps: "Step by step",
    tips: "Important tips",
    next: "Next step",
    center: "Open the full Help Center",
    empty: "No matching guide found.",
  },
};

export default function HelpDrawer({ open, onOpenChange, platform = false }) {
  const { locale } = useI18n();
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const labels = COPY[locale] || COPY.en;
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const available = useMemo(() => visibleHelpArticles({
    locale,
    pathname: location.pathname,
    can: platform ? null : auth.can,
    modules: platform ? [] : auth.modules,
    tenantRole: platform ? null : auth.tenantRole,
    platform,
  }), [locale, location.pathname, platform, auth.can, auth.modules, auth.tenantRole]);

  const results = useMemo(() => searchHelpArticles(available, query), [available, query]);
  const contextual = available.find((item) => item.contextual) || available[0];
  const selected = results.find((item) => item.id === selectedId) || (query ? results[0] : contextual);

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedId(null);
    }
  }, [open, location.pathname]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex h-full w-full flex-col p-0 sm:max-w-xl" data-testid="help-drawer">
        <SheetHeader className="border-b px-6 py-5 pr-12 text-left">
          <SheetTitle className="flex items-center gap-2">
            <CircleHelp className="h-5 w-5 text-primary" />{labels.title}
          </SheetTitle>
          <SheetDescription>{labels.description}</SheetDescription>
        </SheetHeader>

        <div className="border-b px-6 py-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              value={query}
              onChange={(event) => { setQuery(event.target.value); setSelectedId(null); }}
              placeholder={labels.search}
              aria-label={labels.search}
              className="pl-9"
              data-testid="help-search"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {selected ? (
            <>
              <Badge variant="secondary" className="mb-4">
                {selected.contextual && !query ? labels.context : labels.all}
              </Badge>
              <HelpArticle article={selected} labels={labels} compact />

              {results.length > 1 && (
                <section className="mt-7 border-t pt-5">
                  <h3 className="mb-3 text-sm font-semibold">{labels.all}</h3>
                  <div className="grid gap-2">
                    {results.filter((item) => item.id !== selected.id).slice(0, 8).map((item) => (
                      <button
                        key={item.id}
                        onClick={() => setSelectedId(item.id)}
                        className="rounded-lg border p-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        data-testid={`help-result-${item.id}`}
                      >
                        <span className="block text-sm font-semibold">{item.title}</span>
                        <span className="mt-1 block text-xs leading-5 text-muted-foreground">{item.summary}</span>
                      </button>
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="flex min-h-48 flex-col items-center justify-center text-center text-muted-foreground">
              <BookOpen className="mb-3 h-8 w-8" />
              <p className="text-sm">{labels.empty}</p>
            </div>
          )}
        </div>

        {!platform && (
          <div className="border-t p-4">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => {
                const id = selected?.id;
                onOpenChange(false);
                navigate(id ? `/help?article=${encodeURIComponent(id)}` : "/help");
              }}
              data-testid="open-help-center"
            >
              <BookOpen className="mr-2 h-4 w-4" />{labels.center}
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
